// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import {
  PolicyEffectType,
  PolicyDraft,
  StatementService,
  StatementDraft,
  StatementResource,
  StatementAction
} from '@app/core';
import { PolicyFormActionsUnion, PolicyFormActionTypes } from './policy-form.actions';

export interface StatementSelections {
  effect?: PolicyEffectType;
  service?: StatementService;
  resource?: StatementResource;
  actions?: StatementAction[];
  serviceName?: string;
  resourceName?: string;
  actionNames?: string;
  statement_id?: any;
}

export interface PolicyFormState {
  name: string;
  description: string;
  statements: StatementSelections[];
}

const initialState = {
  name: '',
  description: '',
  statements: []
};

export function policyFormReducer(
  state: Readonly<PolicyFormState> = initialState,
  action: PolicyFormActionsUnion
): Readonly<PolicyFormState> {
  switch (action.type) {
    case PolicyFormActionTypes.SET_POLICY_DETAILS:
      return {
        ...state,
        ...action.payload
      };
    case PolicyFormActionTypes.ADD_STATEMENT:
      return {
        ...state,
        statements: [
          ...state.statements,
          { statement_id: action.payload }
        ]
      };
      case PolicyFormActionTypes.ADD_STATEMENT_WITH_EFFECT:
      return {
        ...state,
        statements: [
          ...state.statements,
          { statement_id: action.payload, effect: PolicyEffectType.ALLOW }
        ]
      };
    case PolicyFormActionTypes.REMOVE_STATEMENT:
      return {
        ...state,
        statements: removeStatementAtIndex(state.statements, action.payload.statementIdx)
      };
    case PolicyFormActionTypes.SELECT_EFFECT:
      return {
        ...state,
        statements: updateStatement(state.statements, action.payload.statementIdx, {
          effect: action.payload.effect
        })
      };
    case PolicyFormActionTypes.SELECT_SERVICE:
      return {
        ...state,
        statements: updateStatement(state.statements, action.payload.statementIdx, {
          service: action.payload.service,
          serviceName: action.payload.service && action.payload.service.name || ''
        })
      };
    case PolicyFormActionTypes.SELECT_RESOURCE:
      return {
        ...state,
        statements: updateStatement(state.statements, action.payload.statementIdx, {
          resource: action.payload.resource,
          resourceName: action.payload.resource && action.payload.resource.name || '',
          actions: [],
          actionNames: ''
        })
      };
    case PolicyFormActionTypes.SELECT_ACTION:
      return {
        ...state,
        statements: updateStatement(state.statements, action.payload.statementIdx, {
          actions: action.payload.actions,
          actionNames: action.payload.actions && action.payload.actions.map(ac => ac.name).join(' ,')
        })
      };
    case PolicyFormActionTypes.CLEAR:
      return initialState;
  }

  return state;
}

export function getPolicyFormDetails(state: PolicyFormState) {
  return {
    name: state.name,
    description: state.description
  };
}

export function getPolicyDraft(state: PolicyFormState): PolicyDraft {
  const statements = state.statements.map(selectionToStatement).filter(s => s !== undefined) as StatementDraft[];
  return {
    ...getPolicyFormDetails(state),
    statements
  };
}
export function getStatementSelections(state: PolicyFormState) {
  return state.statements;
}

function removeStatementAtIndex(statements: StatementSelections[], idx: number) {
  if (statements.length <= idx) {
    return statements;
  }
  const statementsShallowCopy = [...statements];
  statementsShallowCopy.splice(idx, 1);
  return statementsShallowCopy;
}

function updateStatement(statements: StatementSelections[], idxToUpdate: number, updates: StatementSelections) {
  return statements.map((statement: StatementSelections, idx: number) => {
    if (idx !== idxToUpdate) {
      return statement;
    }
    return {
      ...statement,
      ...updates
    };
  });
}

function selectionToStatement({ effect, resource, actions, service, statement_id }: StatementSelections): StatementDraft | undefined {
  if (effect === undefined || resource === undefined || actions === undefined || service === undefined) {
    return undefined;
  }

  const actionsArr: string[] = [];
  actions.forEach(e => {
    actionsArr.push(e.id.toString());
  });

  return {
    effect: effect,
    resource_id: resource.id,
    actions: actionsArr,
    statement_id: statement_id
  };
}



// WEBPACK FOOTER //
// ./src/app/access-management/state/policy-form.reducer.ts